import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Send, Plus, Trash2, Menu, X, LogOut, Sparkles, Loader2, Crown,
  Trophy, Zap, User as UserIcon, Paperclip, Image as ImageIcon,
  FileText, File, Copy, Check, MoreHorizontal, Search, ArrowRight, Globe, ExternalLink,
  Mic, CreditCard, Bot, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo, LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Markdown } from "@/components/Markdown";
import { ModelPicker } from "@/components/ModelPicker";
import { AuthGateModal } from "@/components/AuthGateModal";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { Seo } from "@/components/Seo";
import { ShareChatModal } from "@/components/ShareChatModal";
import { useAuth } from "@/contexts/AuthContext";
import {
  listConversations, getConversation, sendChat, deleteConversation,
} from "@/lib/api";

const PLAN_BADGE = {
  free: { label: "Free", icon: Sparkles },
  standard: { label: "Standard", icon: Zap },
  pro: { label: "Pro", icon: Crown },
  pro_max: { label: "Pro Max", icon: Trophy },
};

const MAX_IMAGE_MB = 5;
const MAX_FILE_MB = 20;
const ACCEPTED_TYPES = "image/*,.pdf,.txt,.md,.csv,.json,.js,.ts,.jsx,.tsx,.py,.go,.rs,.java,.c,.cpp,.html,.css";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Chat() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { user, logout, refreshUser } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [authGateOpen, setAuthGateOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  // The most recent submitted message — used by ThinkingBubble to predict
  // whether web-search will fire (so it can show "Searching the web…").
  const [lastSentMessage, setLastSentMessage] = useState("");

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await listConversations();
      setConversations(res.data.conversations || []);
    } catch (e) {
      console.warn("loadConversations failed:", e);
    }
  }, []);

  const loadConversation = useCallback(async (id) => {
    try {
      const res = await getConversation(id);
      setActiveConv(res.data.conversation);
      setMessages(res.data.messages || []);
    } catch (e) {
      toast.error("Failed to load conversation");
      navigate("/chat");
    }
  }, [navigate]);

  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      refreshUser();
      loadConversations();
    }
  }, [userId, refreshUser, loadConversations]);

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setActiveConv(null);
      setMessages([]);
    }
  }, [conversationId, loadConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [input]);

  // Restore pending message after sign-in (from sessionStorage or in-memory)
  useEffect(() => {
    if (!userId) return;
    let pending = pendingMessage;
    if (!pending) {
      try {
        pending = sessionStorage.getItem("sabzy_pending_message") || "";
        if (pending) sessionStorage.removeItem("sabzy_pending_message");
      } catch (e) {
        console.warn("sessionStorage unavailable:", e);
      }
    }
    if (pending) {
      setInput(pending);
      setPendingMessage("");
      setAuthGateOpen(false);
      // Focus textarea so user can press enter immediately
      setTimeout(() => textareaRef.current?.focus(), 200);
    }
  }, [userId, pendingMessage]);

  const handleNewChat = () => {
    setMessages([]);
    setActiveConv(null);
    setAttachments([]);
    setInput("");
    setLastSentMessage("");
    navigate("/chat");
    setSidebarOpen(false);
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await deleteConversation(id);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (activeConv?.id === id) navigate("/chat");
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // ---- File handling ----
  const addFiles = useCallback(async (files) => {
    const arr = Array.from(files || []);
    const accepted = [];
    for (const f of arr) {
      const isImage = f.type.startsWith("image/");
      const limit = isImage ? MAX_IMAGE_MB : MAX_FILE_MB;
      if (f.size > limit * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${limit}MB limit`);
        continue;
      }
      try {
        const data = await fileToBase64(f);
        const preview = isImage ? await fileToDataUrl(f) : null;
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: f.name,
          size: f.size,
          mime_type: f.type || "application/octet-stream",
          kind: isImage ? "image" : "file",
          data,
          preview,
        });
      } catch (e) {
        toast.error(`Failed to read ${f.name}`);
      }
    }
    if (accepted.length) {
      setAttachments((a) => [...a, ...accepted]);
    }
  }, []);

  const handleFilePicker = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((a) => a.filter((x) => x.id !== id));
  };

  // Drag and drop
  useEffect(() => {
    const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
    const handleDragLeave = (e) => {
      if (e.target === document || e.target === document.body) setDragging(false);
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    };
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [addFiles]);

  // Paste images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === "file") {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if ((!input.trim() && attachments.length === 0) || sending) return;

    // GATE: Anonymous users must sign up to send their first message
    if (!user) {
      setPendingMessage(input);
      setAuthGateOpen(true);
      return;
    }

    const userMsg = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: input,
      created_at: new Date().toISOString(),
      race_metadata: attachments.length ? {
        attachments: attachments.map((a) => ({
          name: a.name, mime_type: a.mime_type, kind: a.kind,
          size: a.size, data: a.kind === "image" ? a.preview : null,
        })),
      } : null,
    };
    setMessages((m) => [...m, userMsg]);
    const messageText = input;
    const sendingAttachments = attachments;
    setInput("");
    setAttachments([]);
    setSending(true);

    // Detect "build a website / landing page / HTML" intent and route through Sabzy Agent
    // instead of plain chat. Keeps everything inline — no separate panel.
    const buildRe = /\b(build|create|make|generate|design|code)\b.*(?:landing\s*page|website|web\s*page|site|html|portfolio|one[\s-]pager|home\s*page|app|dashboard|form)\b/i;
    const isBuildIntent = buildRe.test(messageText);

    if (isBuildIntent) {
      try {
        const { runAgent } = await import("@/lib/api");
        const placeholder = {
          id: `agent-${Date.now()}`,
          role: "assistant",
          content: "🛠 Building with Sabzy Agent…",
          model: "claude-sonnet-4-6",
          model_label: "Sabzy Agent",
          created_at: new Date().toISOString(),
          loading_agent: true,
        };
        setMessages((m) => [...m, placeholder]);
        const res = await runAgent(messageText, false);
        const data = res.data;
        const indexFile = data.files?.find((f) => /index\.html?$/i.test(f.path));
        let srcDoc = "";
        if (indexFile) {
          srcDoc = indexFile.content;
          (data.files || []).filter((f) => /\.css$/i.test(f.path)).forEach((f) => {
            const re = new RegExp(`<link[^>]*href=["']\\.?\\/?${f.path.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}["'][^>]*>`, "gi");
            srcDoc = srcDoc.replace(re, `<style>\n${f.content}\n</style>`);
          });
          (data.files || []).filter((f) => /\.js$/i.test(f.path)).forEach((f) => {
            const re = new RegExp(`<script[^>]*src=["']\\.?\\/?${f.path.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}["'][^>]*></script>`, "gi");
            srcDoc = srcDoc.replace(re, `<script>\n${f.content}\n</script>`);
          });
        }
        setMessages((m) => m.map((mm) => mm.id === placeholder.id ? {
          ...mm,
          content: data.summary || "Done.",
          loading_agent: false,
          agent_artifact: {
            id: data.id,
            title: data.title,
            files: data.files,
            srcDoc,
            next_steps: data.next_steps,
          },
        } : mm));
      } catch (err) {
        setMessages((m) => m.filter((mm) => !mm.loading_agent));
        if (err.response?.status === 402) {
          toast.error("Sabzy Agent requires Pro or Pro Max — upgrade to build sites in chat", {
            action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
          });
        } else {
          toast.error(err.response?.data?.detail || "Build failed");
        }
      } finally {
        setSending(false);
        textareaRef.current?.focus();
      }
      return;
    }

    try {
      const payload = {
        conversation_id: activeConv?.id,
        message: messageText,
        model: selectedModel || undefined,
        web_search: null, // null = backend auto-detects from the query
        attachments: sendingAttachments.map(({ name, mime_type, kind, data, size }) => ({
          name, mime_type, kind, data, size,
        })),
      };
      setLastSentMessage(messageText);
      const res = await sendChat(payload);
      const ai = res.data.message;
      setMessages((m) => [...m, {
        id: ai.id,
        role: "assistant",
        content: ai.content,
        model: ai.model,
        model_label: ai.model_label,
        race_metadata: {
          elapsed_seconds: ai.elapsed_seconds,
          race: ai.race,
          web_search: ai.web_search,
          sources: ai.sources,
        },
        created_at: new Date().toISOString(),
      }]);

      if (!activeConv?.id && res.data.conversation_id) {
        window.history.replaceState(null, "", `/chat/${res.data.conversation_id}`);
        setActiveConv({ id: res.data.conversation_id, title: messageText.slice(0, 60) });
      }
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
      setMessages((m) => m.filter((mm) => mm.id !== userMsg.id));
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const planInfo = PLAN_BADGE[user?.plan || "free"];
  const PlanIcon = planInfo.icon;
  const isAnonymous = !user;

  const filteredConvs = search
    ? conversations.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase()))
    : conversations;

  return (
    <div className="chat-shell flex overflow-hidden bg-background text-foreground">
      <Seo
        title="Chat with Sabzy AI — ChatGPT, Claude & Gemini with Web Search"
        description="Start chatting with Sabzy AI right now. ChatGPT Astra, GPT-5.6, Claude Opus 5, Gemini 3.1 Pro and 15 more models with racing mode, live web search and multimodal vision. Free, no card."
        path="/chat"
      />
      {/* ===== Sidebar ===== */}
      <aside
        className={`safe-top fixed inset-y-0 left-0 z-40 flex w-[288px] max-w-[85vw] flex-col border-r border-border/50 bg-card/95 backdrop-blur-2xl transition-transform duration-300 ease-[cubic-bezier(.16,1,.3,1)] lg:relative lg:w-[272px] lg:translate-x-0 lg:bg-card/40 ${
          sidebarOpen ? "translate-x-0 shadow-[24px_0_80px_-20px_rgba(0,0,0,.9)]" : "-translate-x-full"
        }`}
        data-testid="chat-sidebar"
      >
        {/* Sidebar header */}
        <div className="flex h-14 items-center justify-between px-4">
          <Logo />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* New chat */}
        <div className="px-3 pb-2 pt-3">
          <Button
            onClick={handleNewChat}
            className="group h-11 w-full justify-between rounded-xl border border-border/70 bg-background/60 px-3.5 text-sm font-medium text-foreground shadow-[inset_0_1px_0_hsl(var(--foreground)/0.04)] transition hover:border-primary/40 hover:bg-secondary/70"
            data-testid="new-chat-btn"
          >
            <span className="flex items-center gap-2.5"><Plus className="h-4 w-4 text-primary" /> New chat</span>
            <kbd className="hidden rounded-md border border-border/60 bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground lg:inline">⌘K</kbd>
          </Button>
        </div>

        {/* Search */}
        {conversations.length > 4 && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search chats"
                className="h-10 rounded-xl border-border/50 bg-background/50 pl-9 text-[13px] focus-visible:border-primary/50 focus-visible:ring-0"
                data-testid="conv-search-input"
              />
            </div>
          </div>
        )}

        {/* Conv list (only when logged in) */}
        {user ? (
          <ScrollArea className="flex-1 px-2">
            <div className="space-y-0.5 px-1 pb-2">
              <div className="label-mono px-2 pb-1.5 pt-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                Recent
              </div>
              {filteredConvs.length === 0 && (
                <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                  {search ? "No matches" : "No chats yet"}
                </div>
              )}
              {filteredConvs.map((c) => {
                const isActive = activeConv?.id === c.id;
                return (
                  <div
                    key={c.id}
                    className={`group relative flex min-h-[44px] items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-secondary/90 text-foreground shadow-[inset_2px_0_0_hsl(var(--primary))]"
                        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                    }`}
                  >
                    <button
                      onClick={() => { navigate(`/chat/${c.id}`); setSidebarOpen(false); }}
                      className="flex-1 truncate text-left text-[13px]"
                      data-testid={`conv-item-${c.id}`}
                    >
                      {c.title || "New chat"}
                    </button>
                    <button
                      onClick={(e) => handleDelete(c.id, e)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground opacity-60 transition hover:bg-background/60 hover:text-destructive lg:opacity-0 lg:group-hover:opacity-100"
                      data-testid={`delete-conv-${c.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex-1 px-4 py-6" data-testid="anon-signin-cta">
            <div className="rounded-xl border border-border/60 bg-secondary/40 p-4">
              <div className="mb-1.5 text-sm font-semibold">
                Save your chats
              </div>
              <div className="mb-3.5 text-xs leading-relaxed text-muted-foreground">
                Sign in to keep history, upload files, and unlock ChatGPT Astra, Claude Opus 5 & Gemini Pro.
              </div>
              <Button
                onClick={() => navigate("/login")}
                className="h-9 w-full rounded-lg text-xs font-medium"
                data-testid="anon-signin-btn"
              >
                Sign in <ArrowRight className="ml-1.5 h-3 w-3" />
              </Button>
              <button
                onClick={() => navigate("/signup")}
                className="mt-2 w-full text-center text-[11px] text-muted-foreground transition hover:text-primary"
                data-testid="anon-signup-btn"
              >
                Create free account
              </button>
            </div>
          </div>
        )}

        {/* Plan + user block (only logged in) */}
        {user && (
        <div className="safe-bottom border-t border-border/50 p-2">
          {/* Voice cloning CTA — featured */}
          <button
            onClick={() => navigate("/voice")}
            className="tracing-beam group mb-2 flex min-h-[52px] w-full items-center justify-between rounded-xl border border-primary/25 bg-gradient-to-br from-primary/12 via-card to-card px-3 py-2.5 transition hover:border-primary/40"
            data-testid="sidebar-voice-cta"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="grid h-7 w-7 place-items-center rounded-md bg-primary/20 text-primary">
                <Mic className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 text-left">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium leading-tight">Clone your voice</span>
                  <span className="rounded-full bg-emerald-500/15 px-1.5 py-0 text-[9px] font-bold uppercase text-emerald-500">New</span>
                </div>
                <div className="text-[11px] text-muted-foreground">OpenVoice V2 · 6 langs</div>
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
          </button>

          <button
            onClick={() => navigate("/upgrade")}
            className="mb-1 flex min-h-[44px] w-full items-center justify-between rounded-xl px-3 py-2 transition hover:bg-secondary/60"
            data-testid="upgrade-cta-btn"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="grid h-7 w-7 place-items-center rounded-md bg-primary/15 text-primary">
                <PlanIcon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 text-left">
                <div className="text-xs font-medium leading-tight">{planInfo.label}</div>
                <div className="text-[11px] text-muted-foreground">Upgrade plan</div>
              </div>
            </div>
            <Crown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex min-h-[48px] w-full items-center gap-2.5 rounded-xl px-3 py-2 transition hover:bg-secondary/60"
                data-testid="user-menu-trigger"
              >
                <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-primary to-orange-300 text-primary-foreground shadow-[0_0_0_2px_hsl(var(--background))]">
                  <span className="text-[11px] font-semibold uppercase">
                    {(user?.email || "?")[0]}
                  </span>
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-xs font-medium leading-tight">{user?.email}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {user?.country || "Global"}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/upgrade")} data-testid="menu-upgrade">
                <Crown className="mr-2 h-3.5 w-3.5" /> Plans & codes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/voice")} data-testid="menu-voice">
                <Mic className="mr-2 h-3.5 w-3.5" /> Voice cloning
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing">
                <CreditCard className="mr-2 h-3.5 w-3.5" /> Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        )}
      </aside>

      {/* ===== Main area ===== */}
      <main className="chat-ambient relative flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="safe-top sticky top-0 z-30 grid h-[calc(3.5rem+env(safe-area-inset-top))] grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-border/40 bg-background/70 px-3 backdrop-blur-2xl sm:px-4 lg:px-6">
          {/* Left: hamburger + chat title */}
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-xl lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="h-4 w-4" />
            </Button>
            <div className="hidden min-w-0 lg:block">
              <div className="truncate text-[13px] font-medium text-muted-foreground">
                {activeConv?.title || "New chat"}
              </div>
            </div>
          </div>

          {/* Center: BRAND + model picker (the focal point) */}
          <div className="flex items-center justify-center gap-2.5">
            <Link to="/" className="flex items-center gap-2.5 transition hover:opacity-80" data-testid="header-brand">
              <LogoMark size={26} />
              <span className="flex items-baseline leading-none">
                <span
                  className="text-[18px] font-semibold tracking-[-0.025em] text-foreground"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  sabzy
                </span>
                <span
                  className="text-[18px] font-semibold tracking-[-0.025em] text-primary"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  .ai
                </span>
              </span>
            </Link>
            <span className="hidden h-4 w-px bg-border/60 sm:inline-block" />
            <div className="hidden sm:block">
              <ModelPicker
                currentPlan={user?.plan || "free"}
                isAnonymous={isAnonymous}
                onChange={setSelectedModel}
              />
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center justify-end gap-2">
            <div className="sm:hidden">
              <ModelPicker
                currentPlan={user?.plan || "free"}
                isAnonymous={isAnonymous}
                onChange={setSelectedModel}
              />
            </div>
            {/* Share button — only when viewing a saved conversation with messages */}
            {user && activeConv?.id && messages.length > 0 && (
              <Button
                onClick={() => setShareOpen(true)}
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                data-testid="share-chat-btn"
                title="Share this chat"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Share</span>
              </Button>
            )}
            {isAnonymous && (
              <Button
                onClick={() => navigate("/login")}
                size="sm"
                className="h-8 rounded-lg px-3.5 text-xs font-medium"
                data-testid="header-signin-btn"
              >
                Sign in
              </Button>
            )}
            <ThemeToggle />
          </div>
        </header>

        {/* Drop zone indicator */}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-30 m-3 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm">
            <div className="text-center">
              <Paperclip className="mx-auto mb-2 h-8 w-8 text-primary" />
              <div className="text-base font-medium text-primary">Drop to attach</div>
              <div className="text-xs text-muted-foreground">Images, PDFs, text files</div>
            </div>
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="thin-scroll flex-1">
          <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:py-10 xl:max-w-4xl">
            {messages.length === 0 && !sending && (
              <EmptyState
                onPick={(t) => { setInput(t); textareaRef.current?.focus(); }}
                name={user?.email?.split("@")[0]}
              />
            )}
            {messages.map((m, i) => (
              <MessageBubble
                key={m.id}
                msg={m}
                isLast={i === messages.length - 1}
                userEmail={user?.email}
              />
            ))}
            {sending && <ThinkingBubble plan={user?.plan} webSearch={predictWebSearch(lastSentMessage)} racing={selectedModel === "race"} />}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* ===== Composer ===== */}
        <div className="relative bg-gradient-to-t from-background via-background/95 to-transparent px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-6 sm:px-6 lg:pb-6">
          <form onSubmit={handleSend} className="mx-auto w-full max-w-3xl xl:max-w-4xl" data-testid="chat-composer">
            <div className="composer-deck relative overflow-hidden rounded-[22px]">
              {/* Attachment chips */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 border-b border-border/40 p-3" data-testid="attachment-chips">
                  {attachments.map((a) => (
                    <AttachmentChip key={a.id} att={a} onRemove={() => removeAttachment(a.id)} />
                  ))}
                </div>
              )}

              {/* Textarea */}
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                onPaste={handlePaste}
                placeholder="Ask anything…"
                rows={1}
                className="max-h-[40vh] min-h-[56px] resize-none border-0 bg-transparent px-4 py-4 text-[16px] leading-relaxed focus-visible:ring-0 placeholder:text-muted-foreground/45 sm:px-5"
                data-testid="chat-input"
              />

              {/* Bottom row: action buttons + send */}
              <div className="flex items-center justify-between gap-2 px-2 pb-2 sm:px-2.5 sm:pb-2.5">
                <div className="flex items-center gap-0.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_TYPES}
                    onChange={handleFilePicker}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="attach-file-btn"
                    aria-label="Attach file"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.accept = "image/*";
                        fileInputRef.current.click();
                        // Reset accept after a tick
                        setTimeout(() => {
                          if (fileInputRef.current) fileInputRef.current.accept = ACCEPTED_TYPES;
                        }, 100);
                      }
                    }}
                    data-testid="attach-image-btn"
                    aria-label="Attach image"
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  {/* Web search is fully automatic now — the backend triggers it based on
                      the query (real-time keywords / year refs / question patterns). No
                      manual toggle. The "Sources used" badge appears below the answer
                      whenever it actually fires. */}
                  {attachments.length > 0 && (
                    <span className="ml-1 hidden text-xs text-muted-foreground sm:inline">
                      {attachments.length} attached
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="hidden text-[11px] text-muted-foreground/60 md:inline">
                    <kbd className="rounded border border-border/60 px-1 font-mono text-[10px]">↵</kbd> send · <kbd className="rounded border border-border/60 px-1 font-mono text-[10px]">⇧↵</kbd> newline
                  </span>
                  <Button
                    type="submit"
                    size="icon"
                    disabled={(!input.trim() && attachments.length === 0) || sending}
                    className="h-10 w-10 rounded-full shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.8)] transition-all hover:scale-105 hover:shadow-[0_10px_30px_-8px_hsl(var(--primary)/0.9)] active:scale-95 disabled:opacity-30 disabled:shadow-none"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-2.5 hidden text-center text-[11px] text-muted-foreground/60 sm:block">
              Sabzy can make mistakes. Verify important info.
            </div>
          </form>
        </div>
      </main>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm animate-fade-in lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Auth gate (anonymous users · first message) */}
      <AuthGateModal
        open={authGateOpen}
        onClose={() => setAuthGateOpen(false)}
        pendingMessage={pendingMessage}
      />

      {/* Public-share modal */}
      <ShareChatModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        conversation={activeConv}
        onShareStateChange={(s) => setActiveConv((c) => c ? { ...c, ...s } : c)}
      />

      {/* Floating support */}
      <ChatSupportWidget />
    </div>
  );
}

/* ================ Components ================ */

function AttachmentChip({ att, onRemove }) {
  const isImage = att.kind === "image";
  return (
    <div
      className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border border-border bg-background/60 pl-1.5 pr-2 py-1.5 text-xs"
      data-testid={`attachment-chip-${att.id}`}
    >
      {isImage && att.preview ? (
        <div className="h-10 w-10 overflow-hidden rounded-lg">
          <img src={att.preview} alt={att.name} className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary">
          {att.mime_type === "application/pdf" ? <FileText className="h-4 w-4" /> : <File className="h-4 w-4" />}
        </div>
      )}
      <div className="max-w-[140px]">
        <div className="truncate text-[11px] font-medium">{att.name}</div>
        <div className="text-[10px] text-muted-foreground">
          {(att.size / 1024).toFixed(1)} KB
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 grid h-5 w-5 place-items-center rounded-full bg-background/80 text-muted-foreground transition hover:bg-destructive hover:text-destructive-foreground"
        aria-label="Remove"
        data-testid={`remove-attachment-${att.id}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function MessageBubble({ msg, userEmail }) {
  const isUser = msg.role === "user";
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const attachments = msg.race_metadata?.attachments;

  if (isUser) {
    return (
      <div className="msg-in mb-7 flex justify-end" data-testid={`msg-${msg.id}`}>
        <div className="max-w-[88%] space-y-2 sm:max-w-[78%]">
          {attachments?.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2">
              {attachments.map((a, i) => (
                <MessageAttachment key={a.url || a.name || `att-${i}`} att={a} />
              ))}
            </div>
          )}
          {msg.content && (
            <div className="whitespace-pre-wrap rounded-[20px] rounded-tr-md border border-border/60 bg-secondary/80 px-4 py-3 text-[15.5px] leading-relaxed text-foreground shadow-[0_8px_24px_-16px_rgba(0,0,0,.8)]" data-testid="chat-user-message">
              {msg.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="msg-in group mb-9 flex gap-3 sm:gap-4" data-testid={`msg-${msg.id}`}>
      {/* Avatar */}
      <div className="mt-0.5 hidden h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-orange-300 text-primary-foreground shadow-[0_0_20px_-6px_hsl(var(--primary)/0.8)] sm:grid">
        <span className="text-xs font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>S</span>
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">Sabzy</span>
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${providerClass(msg.model)}`} data-testid="chat-model-badge">
            {msg.model_label || displayModel(msg.model)}
          </span>
          {msg.race_metadata?.elapsed_seconds && (
            <>
              <span className="opacity-50">·</span>
              <span>{msg.race_metadata.elapsed_seconds}s</span>
            </>
          )}
          {msg.race_metadata?.web_search && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-primary">
              <Globe className="h-2.5 w-2.5" />
              Web
            </span>
          )}
        </div>
        <div className="text-[15.5px] leading-[1.75] text-foreground/95" data-testid="chat-assistant-message">
          <Markdown content={msg.content} />
        </div>
        {msg.agent_artifact && <ArtifactPanel artifact={msg.agent_artifact} />}
        {msg.race_metadata?.sources?.length > 0 && (
          <SourcesList sources={msg.race_metadata.sources} />
        )}
        {msg.race_metadata?.race && msg.race_metadata.race.length > 1 && (
          <RaceReveal race={msg.race_metadata.race} />
        )}
        {/* Hover actions */}
        <div className="flex items-center gap-1 pt-1 opacity-70 transition lg:opacity-0 lg:group-hover:opacity-100">
          <button
            onClick={copy}
            className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            data-testid={`copy-msg-${msg.id}`}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageAttachment({ att }) {
  if (att.kind === "image" && att.data) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card" style={{ maxWidth: 260 }}>
        <img src={att.data} alt={att.name} className="h-auto w-full" />
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs">
      <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
        {att.mime_type === "application/pdf" ? <FileText className="h-3.5 w-3.5" /> : <File className="h-3.5 w-3.5" />}
      </div>
      <div>
        <div className="font-medium">{att.name}</div>
        {att.size && (
          <div className="text-[10px] text-muted-foreground">{(att.size / 1024).toFixed(1)} KB</div>
        )}
      </div>
    </div>
  );
}

function SourcesList({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-border/60 bg-secondary/30 p-3" data-testid="sources-list">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Globe className="h-3 w-3 text-primary" />
        Sources · {sources.length}
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {sources.map((s, i) => (
          <a
            key={s.url || `src-${i}`}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 rounded-lg bg-background/60 p-2 text-xs transition hover:bg-background"
            data-testid={`source-link-${i}`}
          >
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-primary/15 text-[10px] font-semibold text-primary">
              {i + 1}
            </span>
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-[12px] font-medium text-foreground group-hover:text-primary">
                {s.title || s.url}
              </div>
              <div className="truncate text-[10px] text-muted-foreground">
                {(() => { try { return new URL(s.url).hostname.replace(/^www\./, ""); } catch { return s.url; } })()}
              </div>
            </div>
            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
          </a>
        ))}
      </div>
    </div>
  );
}

function RaceReveal({ race }) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {race.map((r, i) => (
        <div
          key={r.model || `race-${i}`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
            r.winner
              ? "border-primary/50 bg-primary/15 text-primary"
              : "border-border bg-secondary/50 text-muted-foreground"
          }`}
          data-testid={`race-result-${i}`}
        >
          {r.winner && <Trophy className="h-2.5 w-2.5" />}
          <span>{r.model_label || displayModel(r.model)}</span>
          {r.elapsed_seconds && <span className="opacity-70">{r.elapsed_seconds}s</span>}
          {r.status === "cancelled" && <span className="opacity-70">·skipped</span>}
        </div>
      ))}
    </div>
  );
}

function ThinkingBubble({ plan, webSearch, racing }) {
  return (
    <div className="msg-in mb-10 flex gap-3 sm:gap-4" data-testid="thinking-bubble">
      <div className="mt-0.5 hidden h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-orange-300 text-primary-foreground animate-pulse-glow sm:grid">
        <span className="text-xs font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>S</span>
      </div>
      <div className="flex-1 space-y-2 pt-0.5">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">Sabzy</span>
          <span className="opacity-50">·</span>
          {webSearch ? (
            <span className="inline-flex items-center gap-1.5 text-primary">
              <Globe className="h-3 w-3 animate-pulse" />
              Searching the web…
            </span>
          ) : (
            <span>{racing ? "Racing 3 models…" : "Thinking…"}</span>
          )}
        </div>
        <div className="space-y-2 pt-1">
          <div className="thinking-bar relative h-2 w-[70%] overflow-hidden rounded-full bg-secondary/80" />
          <div className="thinking-bar relative h-2 w-[45%] overflow-hidden rounded-full bg-secondary/60" style={{ animationDelay: "200ms" }} />
        </div>
      </div>
    </div>
  );
}

function providerClass(m) {
  if (!m) return "provider-sabzy";
  if (m.startsWith("gpt")) return "provider-openai";
  if (m.startsWith("claude")) return "provider-anthropic";
  if (m.startsWith("gemini")) return "provider-gemini";
  return "provider-sabzy";
}

const SUGGESTIONS = [
  { icon: Sparkles, title: "Explain like I'm five", text: "Explain how large language models work, like I'm five." },
  { icon: Globe, title: "Search the live web", text: "What are the biggest AI announcements this week? Cite sources." },
  { icon: FileText, title: "Draft something", text: "Write a crisp cold email pitching an AI automation service to a dental clinic." },
  { icon: Bot, title: "Build with code", text: "Build me a landing page for a coffee shop with a dark, premium look." },
];

function EmptyState({ onPick, name }) {
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return (
    <div className="flex min-h-[52vh] flex-col items-center justify-center pb-6 pt-4 text-center animate-fade-up" data-testid="chat-empty-state">
      <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-primary to-orange-300 text-primary-foreground shadow-[0_0_60px_-12px_hsl(var(--primary)/0.9)]">
        <span className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>S</span>
      </div>
      <h1 className="text-3xl font-medium tracking-[-0.03em] sm:text-4xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {greet}{name ? `, ${name}` : ""}.
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
        ChatGPT, Claude and Gemini — pick a model above, or just start typing.
      </p>
      <div className="mt-8 grid w-full max-w-2xl grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="chat-suggestions">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={s.title}
            onClick={() => onPick(s.text)}
            className="glass-card group flex min-h-[84px] flex-col justify-between rounded-2xl p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-card/80 active:scale-[0.99]"
            style={{ animationDelay: `${i * 60}ms` }}
            data-testid={`chat-suggestion-${i}`}
          >
            <s.icon className="h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-medium">{s.title}</div>
              <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{s.text}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function displayModel(m) {
  if (!m) return "Sabzy AI";
  const map = {
    "gpt-6-astra": "ChatGPT Astra", "gpt-5.6-sol": "GPT-5.6 Sol", "gpt-5.6-terra": "GPT-5.6 Terra",
    "gpt-5.6-luna": "GPT-5.6 Luna", "gpt-5.5": "GPT-5.5", "gpt-5.4": "GPT-5.4", "gpt-5.4-mini": "GPT-5.4 Mini",
    "gpt-5-nano": "GPT-5 Nano", "gpt-4o": "GPT-4o",
    "claude-opus-5": "Claude Opus 5", "claude-opus-4-8": "Claude Opus 4.8", "claude-opus-4-7": "Claude Opus 4.7",
    "claude-sonnet-5": "Claude Sonnet 5", "claude-sonnet-4-6": "Claude Sonnet 4.6", "claude-haiku-4-5-20251001": "Claude Haiku 4.5",
    "gemini-3.8-flash": "Gemini 3.8 Flash", "gemini-3.5-flash": "Gemini 3.5 Flash",
    "gemini-3.1-pro-preview": "Gemini 3.1 Pro", "gemini-3-flash-preview": "Gemini 3 Flash",
  };
  return map[m] || m;
}

/**
 * Lightweight mirror of the backend `needs_web_search()` heuristic. Used only
 * to pick the right "Searching the web…" copy on the thinking bubble so the
 * UX matches what the backend is actually doing. Source of truth stays in
 * /app/backend/web_search.py.
 */
const _WEB_KEYWORDS = [
  // time / now
  "latest", "today", "tonight", "yesterday", "this week", "this month",
  "this year", "current", "currently", "recent", "recently", "right now",
  "live", "ongoing", "breaking", "real-time", "real time", "just now",
  // news
  "news", "headline", "headlines", "trending", "viral", "announcement",
  // markets / crypto
  "stock price", "share price", "market cap", "ipo", "exchange rate",
  "bitcoin", "btc", "ethereum", "eth", "solana", "crypto", "doge",
  "rupee", "dollar to", "to inr", "to usd", "interest rate",
  // sports
  "score", "who won", "winner of", "fixture", "playing 11", "world cup",
  "ipl", "nba", "playoff", "championship",
  // weather / disasters
  "weather", "forecast", "temperature", "rain", "storm", "cyclone",
  "earthquake", "aqi", "air quality",
  // people / politics
  "ceo of", "president of", "prime minister", "election",
  // tech
  "release date", "launch", "launched", "rolling out", "beta",
  "gpt", "chatgpt", "openai", "claude", "anthropic", "gemini", "deepseek",
];
const _WEB_PATTERNS = [
  /\bwhat\s+(?:is|are|was)\s+(?:the\s+)?(?:price|score|status|state|cost|rate|weather|forecast|location)\b/i,
  /\bwhen\s+(?:is|was|did|will)\s+/i,
  /\bwho\s+is\s+(?:the\s+)?(?:current|new|latest|next)\b/i,
];
function predictWebSearch(query) {
  if (!query) return false;
  const q = query.toLowerCase();
  if (_WEB_KEYWORDS.some((k) => q.includes(k))) return true;
  if (_WEB_PATTERNS.some((p) => p.test(q))) return true;
  // year reference (current ± 2)
  const y = new Date().getFullYear();
  return new RegExp(`\\b(?:${y - 2}|${y - 1}|${y}|${y + 1})\\b`).test(q);
}


function ArtifactPanel({ artifact }) {
  const [tab, setTab] = useState(artifact.srcDoc ? "preview" : "files");
  const [activeFile, setActiveFile] = useState(0);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const f = artifact.files?.[activeFile];

  const openInNew = () => {
    if (!artifact.srcDoc) return;
    const blob = new Blob([artifact.srcDoc], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-card/60" data-testid={`artifact-${artifact.id}`}>
      <div className="flex items-center justify-between border-b border-border bg-secondary/30 px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <Sparkles className="h-3 w-3 text-emerald-500" />
          <span className="font-semibold">{artifact.title}</span>
          <span className="text-muted-foreground">· {artifact.files?.length} file{artifact.files?.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-1">
          {artifact.srcDoc && (
            <button
              onClick={() => setTab(tab === "preview" ? "files" : "preview")}
              className={`rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition ${
                tab === "preview" ? "bg-emerald-500/20 text-emerald-500" : "text-muted-foreground hover:bg-secondary"
              }`}
              data-testid={`artifact-preview-toggle-${artifact.id}`}
            >
              {tab === "preview" ? "Preview" : "Code"}
            </button>
          )}
          <a
            href={`${apiBase}/api/agent/runs/${artifact.id}/download`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground transition hover:bg-primary/90"
            data-testid={`artifact-download-${artifact.id}`}
          >
            ↓ ZIP
          </a>
          {artifact.srcDoc && (
            <button onClick={openInNew} className="rounded-md px-1.5 py-1 text-muted-foreground transition hover:bg-secondary" title="Open in new tab" data-testid={`artifact-open-new-${artifact.id}`}>
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {tab === "preview" && artifact.srcDoc ? (
        <iframe
          title={artifact.title}
          srcDoc={artifact.srcDoc}
          sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
          className="h-[420px] w-full border-0 bg-white"
          data-testid={`artifact-iframe-${artifact.id}`}
        />
      ) : (
        <div>
          <div className="flex overflow-x-auto border-b border-border bg-secondary/20">
            {artifact.files?.map((file, i) => (
              <button
                key={file.path}
                onClick={() => setActiveFile(i)}
                className={`shrink-0 border-r border-border px-3 py-1.5 text-[10px] transition ${
                  i === activeFile ? "bg-card font-semibold" : "text-muted-foreground hover:bg-card/60"
                }`}
                data-testid={`artifact-file-${artifact.id}-${i}`}
              >
                {file.path}
              </button>
            ))}
          </div>
          {f && (
            <pre className="max-h-[360px] overflow-auto p-3 text-[11px] leading-relaxed font-mono" data-testid={`artifact-code-${artifact.id}`}>
              <code className="whitespace-pre-wrap break-words">{f.content}</code>
            </pre>
          )}
        </div>
      )}

      {artifact.next_steps?.length > 0 && (
        <div className="border-t border-border bg-secondary/20 px-3 py-2">
          <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Next steps</div>
          <ul className="space-y-0.5 text-[11px] text-muted-foreground">
            {artifact.next_steps.map((s, i) => (
              <li key={`step-${i}-${s.slice(0, 24)}`}>{i + 1}. {s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

